import type { NetworkId } from "@alch/dx-entities/network/network";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { ComposerFormState } from "./lib/form.js";
import {
  composerFormSchemaForMethod,
  defaultValuesForMethod,
} from "./lib/form.js";
import type { ComposerMethodConfig } from "./lib/methods.js";
import {
  getMethodConfigsByName,
  getUniqueMethodConfig,
} from "./lib/methods.js";
import { parseStateFromShareUrl } from "./lib/sharing.js";

const DEFAULT_METHOD_FILTER = {
  network: "ETH_MAINNET",
  method: "eth_getBlockByNumber",
} as const;

const getFormDefaults = ({
  methodConfigs,
  search = "",
  defaultOverrides,
}: Pick<
  UseComposerFormProps,
  "methodConfigs" | "search" | "defaultOverrides"
>) => {
  const share = parseStateFromShareUrl({ search, methodConfigs });

  let methodConfig: ComposerMethodConfig | undefined = methodConfigs[0];
  let network: NetworkId | undefined = methodConfig?.networks[0];

  const defaultOverrideMethodConfigs = defaultOverrides?.method
    ? getMethodConfigsByName(methodConfigs, defaultOverrides.method)
    : [];
  const defaultOverrideMethodConfig =
    defaultOverrides?.method == null
      ? undefined
      : defaultOverrides.network
        ? getUniqueMethodConfig(methodConfigs, {
            method: defaultOverrides.method,
            network: defaultOverrides.network,
          }) ?? defaultOverrideMethodConfigs[0]
        : defaultOverrideMethodConfigs[0];

  if (share) {
    methodConfig = share.methodConfig;
    network = share.state.network;
  } else if (defaultOverrideMethodConfig) {
    methodConfig = defaultOverrideMethodConfig;
    network =
      defaultOverrides?.network ?? defaultOverrideMethodConfig.networks[0];
  } else {
    const defaultMethodConfig = getUniqueMethodConfig(
      methodConfigs,
      DEFAULT_METHOD_FILTER,
    );

    if (defaultMethodConfig) {
      methodConfig = defaultMethodConfig;
      network = DEFAULT_METHOD_FILTER.network;
    }
  }

  return {
    schema: composerFormSchemaForMethod(methodConfig),
    values: {
      ...defaultValuesForMethod({ methodConfig, network }),
      ...defaultOverrides,
      ...share?.state,
    },
  };
};

interface UseComposerFormProps {
  methodConfigs: ComposerMethodConfig[];
  search?: string;
  defaultOverrides?: Partial<ComposerFormState>;
}

const useComposerForm = ({
  methodConfigs,
  search,
  defaultOverrides,
}: UseComposerFormProps) => {
  const [defaults] = useState(() =>
    getFormDefaults({ methodConfigs, search, defaultOverrides }),
  );
  const [schema, setSchema] = useState(defaults.schema);

  const form = useForm({
    defaultValues: defaults.values,
    resolver: zodResolver(schema),
    mode: "onBlur",
  });

  const network = form.watch("network");
  const method = form.watch("method");
  const app = form.watch("app");

  const selectedMethodConfigs = useMemo(
    () => getMethodConfigsByName(methodConfigs, method),
    [methodConfigs, method],
  );

  const methodConfig = useMemo(
    () => getUniqueMethodConfig(selectedMethodConfigs, { method, network }),
    [selectedMethodConfigs, method, network],
  );

  useEffect(() => {
    const isChangingMethod = form.formState.defaultValues?.method !== method;

    if (!isChangingMethod && methodConfig) {
      // Do not reset the initial state if the methods has not changed
      return;
    } else if (methodConfig) {
      // Reset form state when the method config changes
      form.reset({
        ...defaultValuesForMethod({ methodConfig, network }),
        app,
      });
      setSchema(composerFormSchemaForMethod(methodConfig));
    } else if (selectedMethodConfigs.length > 0) {
      // Change network if the current method is not supported
      form.setValue("network", selectedMethodConfigs[0].networks[0]);
    } else if (methodConfigs.length > 0) {
      // Fallback to the first method if no method config is found
      form.setValue("method", methodConfigs[0].name);
    } else {
      // Can't do anything if there are no method configs. The caller should hide the form if there are no method configs.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore other dependencies
  }, [methodConfig]);

  return form;
};

export default useComposerForm;
